import { useState, useEffect } from 'react';
import { notificationsAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Copy, ExternalLink, Send, Loader2, Check, Phone } from 'lucide-react';

const TRIGGER_LABELS = {
  order_confirmed: 'Order Confirmed', fabric_ordered: 'Fabric Ordered',
  fabric_received: 'Fabric Received', production_started: 'Production Started',
  ready_for_trial: 'Ready for Trial', alterations_complete: 'Alterations Complete',
  ready_for_delivery: 'Ready for Delivery', delivered: 'Delivered',
  payment_received: 'Payment Received', delay_alert: 'Delay Alert',
  birthday_greeting: 'Birthday Greeting', feedback_request: 'Feedback Request',
};

export default function SendNotificationDialog({ open, onOpenChange, orderId, clientId }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState('');
  const [rendered, setRendered] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [messageText, setMessageText] = useState('');

  useEffect(() => {
    if (open) {
      notificationsAPI.listTemplates().then(res => {
        setTemplates((res.data?.templates || []).filter(t => t.is_active));
      }).catch(() => {});
      setRendered(null);
      setSelectedTrigger('');
      setCopied(false);
    }
  }, [open]);

  const handleSelectTemplate = async (trigger) => {
    setSelectedTrigger(trigger);
    setLoading(true);
    setCopied(false);
    try {
      const res = await notificationsAPI.render({
        trigger_event: trigger,
        order_id: orderId,
        client_id: clientId,
      });
      setRendered(res.data);
      setMessageText(res.data?.rendered_text || '');
    } catch { toast.error('Failed to render template'); }
    setLoading(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Message copied to clipboard');
    } catch { toast.error('Copy failed'); }
  };

  const handleOpenWhatsApp = () => {
    if (rendered?.whatsapp_link) {
      window.open(rendered.whatsapp_link, '_blank');
    } else if (rendered?.client_phone) {
      const encoded = encodeURIComponent(messageText);
      const phone = rendered.client_phone.replace(/[^\d]/g, '');
      window.open(`https://wa.me/${phone}?text=${encoded}`, '_blank');
    }
  };

  const handleMarkSent = async () => {
    if (!clientId || !selectedTrigger) return;
    setSending(true);
    try {
      await notificationsAPI.send({
        client_id: clientId,
        order_id: orderId || null,
        trigger_event: selectedTrigger,
        channel: 'whatsapp',
        message_text: messageText,
      });
      toast.success('Notification logged as sent');
      onOpenChange(false);
    } catch { toast.error('Failed to log notification'); }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-notification-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <MessageSquare className="w-4 h-4" /> Send WhatsApp Notification
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template selector */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Select Template</Label>
            <Select value={selectedTrigger} onValueChange={handleSelectTemplate}>
              <SelectTrigger className="mt-1" data-testid="template-selector">
                <SelectValue placeholder="Choose a notification template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.trigger_event} value={t.trigger_event}>
                    {TRIGGER_LABELS[t.trigger_event] || t.trigger_event}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--brand-gold)' }} />
            </div>
          )}

          {/* Rendered message */}
          {rendered && !loading && (
            <>
              {rendered.client_phone && (
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
                  <Phone className="w-3 h-3" />
                  Sending to: <span className="font-medium" style={{ color: 'var(--navy-ink)' }}>{rendered.client_phone}</span>
                </div>
              )}

              <div>
                <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Message</Label>
                <Textarea value={messageText} onChange={e => setMessageText(e.target.value)}
                  className="mt-1 text-sm h-36 font-normal" data-testid="notification-message-text" />
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 gap-1.5 text-xs" onClick={handleCopy}
                  data-testid="copy-message-btn">
                  {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy Message'}
                </Button>
                <Button variant="outline" className="flex-1 gap-1.5 text-xs" onClick={handleOpenWhatsApp}
                  style={{ color: '#25D366', borderColor: '#25D366' }}
                  data-testid="open-whatsapp-btn">
                  <ExternalLink className="w-3.5 h-3.5" /> Open WhatsApp
                </Button>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {rendered && (
            <Button onClick={handleMarkSent} disabled={sending || !messageText}
              className="gap-1.5" style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="mark-sent-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? 'Saving...' : 'Mark as Sent'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
